import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
    getPaymentMethods,
    createPaymentMethod,
    togglePaymentMethod,
    deletePaymentMethod
} from '../../services/paymentMethodService'
import { PaymentMethod } from '../../types/payment-method'
import { Trash2, ToggleLeft, ToggleRight, Plus, CreditCard, Banknote, Smartphone } from 'lucide-react'
import { toast } from 'react-toastify'

export const PaymentMethodSettings = () => {
    const queryClient = useQueryClient()
    const [newMethodName, setNewMethodName] = useState('')
    const [newMethodType, setNewMethodType] = useState<PaymentMethod['type']>('TRANSFER')
    const [isAdding, setIsAdding] = useState(false)

    const { data: methods, isLoading } = useQuery({
        queryKey: ['paymentMethods'],
        queryFn: getPaymentMethods
    })

    const createMutation = useMutation({
        mutationFn: createPaymentMethod,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['paymentMethods'] })
            setNewMethodName('')
            setIsAdding(false)
            toast.success('Método de pago creado')
        },
        onError: (err: any) => {
            toast.error(err.response?.data?.message || 'Error al crear')
        }
    })

    const toggleMutation = useMutation({
        mutationFn: togglePaymentMethod,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['paymentMethods'] })
        }
    })

    const deleteMutation = useMutation({
        mutationFn: deletePaymentMethod,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['paymentMethods'] })
            toast.success('Método de pago eliminado')
        }
    })

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault()
        if (!newMethodName.trim()) return
        createMutation.mutate({ name: newMethodName, type: newMethodType })
    }

    const getIcon = (type: string) => {
        switch (type) {
            case 'CASH': return <Banknote className="w-5 h-5 text-green-600" />
            case 'CARD': return <CreditCard className="w-5 h-5 text-blue-600" />
            case 'TRANSFER': return <Smartphone className="w-5 h-5 text-purple-600" />
            default: return <Banknote className="w-5 h-5" />
        }
    }

    if (isLoading) return <div className="p-4">Cargando...</div>

    return (
        <div className="bg-white rounded-lg shadow p-6 max-w-2xl mx-auto mt-8">
            <div className="flex justify-between items-center mb-6">
                <h2 className="text-xl font-semibold text-gray-800">Medios de Pago</h2>
                <button
                    onClick={() => setIsAdding(!isAdding)}
                    className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 transition"
                >
                    <Plus size={18} />
                    {isAdding ? 'Cancelar' : 'Nuevo Método'}
                </button>
            </div>

            {isAdding && (
                <form onSubmit={handleSubmit} className="mb-6 bg-gray-50 p-4 rounded-md border border-gray-200">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Nombre</label>
                            <input
                                autoFocus
                                type="text"
                                value={newMethodName}
                                onChange={(e) => setNewMethodName(e.target.value)}
                                placeholder="Ej. Bancolombia, Nequi..."
                                className="w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 p-2 border"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Tipo</label>
                            <select
                                value={newMethodType}
                                onChange={(e) => setNewMethodType(e.target.value as any)}
                                className="w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 p-2 border"
                            >
                                <option value="TRANSFER">Digital (App/Transferencia)</option>
                                <option value="CASH">Efectivo</option>
                                <option value="CARD">Tarjeta (Datáfono)</option>
                            </select>
                        </div>
                    </div>
                    <div className="mt-4 flex justify-end">
                        <button
                            type="submit"
                            disabled={createMutation.isPending}
                            className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50"
                        >
                            Guadar
                        </button>
                    </div>
                </form>
            )}

            <div className="space-y-3">
                {methods?.map((method) => (
                    <div key={method.id} className="flex items-center justify-between p-3 border rounded-lg hover:bg-gray-50 transition">
                        <div className="flex items-center gap-3">
                            <div className="p-2 bg-gray-100 rounded-full">
                                {getIcon(method.type)}
                            </div>
                            <div>
                                <p className="font-medium text-gray-900">{method.name}</p>
                                <p className="text-xs text-gray-500">{method.type}</p>
                            </div>
                        </div>

                        <div className="flex items-center gap-2">
                            <button
                                onClick={() => toggleMutation.mutate(method.id)}
                                className={`p-1 rounded-full transition-colors ${method.isActive ? 'text-green-600 hover:text-green-700' : 'text-gray-400 hover:text-gray-500'}`}
                                title={method.isActive ? 'Desactivar' : 'Activar'}
                            >
                                {method.isActive ? <ToggleRight size={28} /> : <ToggleLeft size={28} />}
                            </button>

                            <button
                                onClick={() => {
                                    if (confirm('¿Seguro que deseas eliminar este método de pago?')) {
                                        deleteMutation.mutate(method.id)
                                    }
                                }}
                                className="p-2 text-red-500 hover:bg-red-50 rounded-full transition"
                                title="Eliminar"
                            >
                                <Trash2 size={18} />
                            </button>
                        </div>
                    </div>
                ))}

                {methods?.length === 0 && (
                    <p className="text-center text-gray-500 py-8">No hay medios de pago configurados.</p>
                )}
            </div>
        </div>
    )
}
